import type { CSSProperties, ReactNode } from "react";

import { cn } from "@/lib/utils";

import { f1, MIN_STANDARD } from "./kit";

/** A row of colour keys above a chart or table. */
export function Legend({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap gap-3.5 text-xs">{children}</div>;
}

export function Swatch({ className, style, children }: { className?: string; style?: CSSProperties; children: ReactNode }) {
  return (
    <span className="flex items-center gap-1.5">
      <i aria-hidden="true" className={cn("inline-block size-3 rounded-[2px]", className)} style={style} />
      {children}
    </span>
  );
}

/** The "duty unknown" fill: danger hatching on the surface colour, with a danger outline. */
export const HATCH_CLASS = "border border-ks-danger";
export const HATCH_STYLE: CSSProperties = {
  backgroundImage:
    "repeating-linear-gradient(45deg, var(--color-ks-surface), var(--color-ks-surface) 4px, var(--color-ks-danger) 4px, var(--color-ks-danger) 5px)",
};

/** The colour key of both landed-cost pages; the chart page adds the latest-purchase line. */
export function LandedLegend({ withPurchase = false }: { withPurchase?: boolean }) {
  return (
    <Legend>
      <Swatch className="bg-ks-purple">Raw material</Swatch>
      <Swatch className="bg-ks-purple-2">Duty component</Swatch>
      <Swatch className="bg-ks-grey-1">Transport</Swatch>
      <Swatch className={HATCH_CLASS} style={HATCH_STYLE}>
        {withPurchase ? "Duty unknown" : "Duty unknown (no HS code)"}
      </Swatch>
      {withPurchase ? <Swatch className="h-0 w-[18px] rounded-none border-t-[3px] border-ks-warn">Latest purchase</Swatch> : null}
    </Legend>
  );
}

/** One feature score in a table cell: a short bar against the 7.0 line, then the value to 1 dp. */
export function MiniBar({ value, label }: { value: number; label: string }) {
  const width = Math.max(0, Math.min(10, value)) * 10;
  return (
    <div role="img" aria-label={`${label} ${f1(value)}`} className="flex min-w-24 items-center gap-1.5">
      <div className="relative h-3 min-w-12 flex-1">
        <div
          className={cn("h-full rounded-[2px]", value < MIN_STANDARD ? "bg-ks-grey-1" : "bg-ks-purple")}
          style={{ width: `${width}%` }}
        />
        <div className="absolute -top-0.5 -bottom-0.5 border-l-[1.5px] border-dashed border-ks-ink" style={{ left: "70%" }} />
      </div>
      <span className="w-7 text-right text-xs tabular-nums">{f1(value)}</span>
    </div>
  );
}

/** A share of 100 as an 8px bar under a figure. */
export function CellBar({ percent }: { percent: number }) {
  return (
    <div className="mt-1 h-2 rounded-[2px] bg-ks-grey-4">
      <span className="block h-full rounded-[2px] bg-ks-purple" style={{ width: `${Math.max(0, Math.min(100, percent))}%` }} />
    </div>
  );
}
